/**
 * PersonalThreadView — thread view for the personal inbox.
 *
 * Replaces PersonalEmailDetailPage for emails that have threading data in
 * the communications collection. Shows all messages in a conversation as
 * flat ThreadMessage rows with inline ReplyComposer, inside the shared
 * `ThreadReaderSurface` — the same chrome the unified reader mounts.
 *
 * Falls back to React Query cache search for sequence emails that don't have
 * communications rows yet (Phase 5 backfill is ongoing).
 *
 * Forward navigates to PersonalComposePage with router state (not URL params)
 * — fixes the URL length bug from the old PersonalEmailDetailPage.
 */
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import ThreadMessage from './ThreadMessage';
import { downloadMailAttachment } from './attachmentDownload';
import ThreadReaderToolbar from './ThreadReaderToolbar';
import { reportTagResult } from './BulkTagPanel';
import { conversationTagsOf, tagErrorMessage } from './MailTagPicker';
import ThreadReaderSurface, { ReaderChip } from './ThreadReaderSurface';
import ThreadFooter from './ThreadFooter';
import ReplyComposer from './ReplyComposer';
import { senderName as rowSenderName } from './MailRow';
import ProviderBadge from './ProviderBadge';
import { SURFACES } from './surfaceContract';
import { conversationArchived, conversationBinned } from './mailRowState';
import { mailStateScopeHint, providerChip } from './originIdentity';
import useMailFolders from '../../hooks/useMailFolders';
import {
    resolveSnoozePreset,
    useArchiveMail,
    useMoveMail,
    useReadMail,
    useTagMail,
    useSnoozeMail,
    useImportantMail,
    useStarMail,
} from '../../hooks/useMailState';
import { useInboxThread, useGmailEmailDetail, useEmailTemplates, useSendEmail, useSignatures } from '../../hooks/useInbox';
import { useOutlookEmailDetail } from '../../hooks/useOutlook';
import { usePersonalSenders } from '../../hooks/usePersonalSenders';
import {
    useMarkPersonalInboxRead,
    useReplyToPersonalMessage,
} from '../../hooks/usePersonalInbox';
import { useAuth } from '../../context/AuthContext';
import { buildSignatureHtml, hasAuthoredText } from '../../lib/editorSafeHtml';
import { pickSeedSignature } from '../../lib/signatureSelection';
import { useAttachmentUpload } from '../../hooks/useAttachmentUpload';
import { resolveDisplayZone } from '../../lib/timezone';
import { computeReplyRecipients, resolveReplyTarget } from './inboxRules';
import { modeContract, threadSubject } from './mailComposerModes';
import { InboxError, InboxSpinner, buildForwardHeader } from './inboxUtils';
import { describeSendError } from './sendErrors';
import { markMailNotificationsRead, notifyBellChanged } from '../../lib/bellLinkage';
import { classifyThreadError } from './threadLoadError';
import useBellFocus, { useActiveBellFocus } from '../../hooks/useBellFocus';

/** The id a thread row is addressed by, across all three writers' shapes. */
const idOf = (entry) => entry?.comm_id || entry?.email_id || entry?.tracking_id || null;

/**
 * The domain part of an address, lowercased. '' when there isn't one.
 *
 * Unwraps the `Name <addr@host>` display form first: the personal Outlook sync
 * stores exactly that in `from_email`, and a naive lastIndexOf('@') on it
 * yields `host>` — which never equals the mailbox domain, so every internal
 * thread would have rendered as External.
 */
function domainOf(address) {
    const raw = String(address || '');
    const angled = raw.match(/<([^>]+)>/);
    const addr = angled ? angled[1] : raw;
    const at = addr.lastIndexOf('@');
    return at === -1 ? '' : addr.slice(at + 1).trim().toLowerCase();
}

export default function PersonalThreadView() {
    const { commId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { user, getAuthHeader } = useAuth();

    // Received attachments are fetched through the authenticated route and
    // handed to the browser as a blob — see `attachmentDownload.js` for why
    // this is not a plain `<a href>`.
    const downloadAttachment = useCallback(async (entry, attachment) => {
        try {
            await downloadMailAttachment({
                commId: entry.comm_id, attachment, getAuthHeader,
            });
        } catch {
            toast.error(`Could not download ${attachment.filename || 'the attachment'}`);
        }
    }, [getAuthHeader]);
    // #484 — called ONCE at the top level. ReplyComposer renders inside
    // messagesWithBodies.map(...) below; calling a hook there would violate the
    // rules of hooks, which craco.config.js sets to `error` — so it would break
    // the Docker build even though there is no standalone lint gate.
    // Try the backend thread endpoint first
    const {
        data: threadData, isLoading: threadLoading, isError: threadError, error: threadErrorObj, refetch,
    } = useInboxThread(commId);
    // #803 — React Query keeps the last good `data` after a failed refetch,
    // so a thread deleted while it was cached would still render in full,
    // live actions and all. A 404 "Thread not found" means the server no
    // longer has it: the kept rows are not shown.
    const threadGone = classifyThreadError(threadErrorObj) === 'gone';
    const threadRows = !threadGone && threadData?.data?.length ? threadData.data : null;

    // Fallback: search React Query cache for this email (for sequence emails
    // without communications rows)
    const cachedEmail = useMemo(() => {
        if (location.state?.email) return location.state.email;
        if (!commId) return null;

        // Search all inbox-related caches
        const caches = [
            ...queryClient.getQueriesData({ queryKey: ['inbox'] }),
            ...queryClient.getQueriesData({ queryKey: ['gmail'] }),
            ...queryClient.getQueriesData({ queryKey: ['outlook'] }),
            ...queryClient.getQueriesData({ queryKey: ['communications'] }),
        ];
        for (const [, data] of caches) {
            const items = Array.isArray(data) ? data : (data?.data || data?.emails || data?.pages?.flat() || []);
            if (!Array.isArray(items)) continue;
            const found = items.find(item =>
                item.comm_id === commId ||
                item.email_id === commId ||
                item.tracking_id === commId ||
                item.communication_id === commId
            );
            if (found) return found;
        }
        return null;
    }, [commId, location.state, queryClient]);

    // The cache fallback exists for sequence emails, which have no
    // communications row (they carry `email_id`, never `comm_id`). A cached
    // COMMUNICATIONS row the server just 404'd is deleted mail, not a
    // fallback (#803).
    const fallbackEmail = cachedEmail && !(threadGone && cachedEmail.comm_id) ? cachedEmail : null;

    // Build the messages array — prefer backend thread, fall back to cache
    const messages = useMemo(() => {
        if (threadRows) return threadRows;
        if (fallbackEmail) return [fallbackEmail];
        return [];
    }, [threadRows, fallbackEmail]);

    // The message this view is anchored on — the seed row from the backend
    // thread when there is one, otherwise the cached list row.
    //
    // The seed has to come from the thread data too, not just the cache. The
    // previous version short-circuited on `threadData?.data?.length` and only
    // ever considered fetching a body when the thread endpoint returned
    // NOTHING. But rows written by the old metadata-only Gmail sync are
    // present in `communications` and carry no `body_html` — so the thread
    // endpoint returned them, the short-circuit fired, and the fetch-on-open
    // path below was unreachable for exactly the messages that needed it. The
    // body rendered as an HTML-escaped one-line snippet in a <pre>, forever.
    const seed = useMemo(() => {
        if (threadRows) {
            const match = threadRows.find((m) => m.comm_id === commId);
            return match || threadRows[0];
        }
        return fallbackEmail;
    }, [threadRows, fallbackEmail, commId]);

    // ── Per-message collapse (plan §12) ────────────────────────────────────
    //
    // Every card but the NEWEST starts collapsed. The parent owns the state
    // rather than each card, for one reason that matters: a collapsed message
    // must not fetch its body, and the fetch lives here. A card that owned its
    // own collapse could not tell this view to hold the request.
    //
    // Derived from `messages`, not `messagesWithBodies` — the latter depends on
    // the fetch this gates, and reading it here would be a cycle. Both hold the
    // same rows in the same order, which is OLDEST-first and enforced
    // server-side (`routes/personal_inbox.py` sorts the thread), so "newest" is
    // the last element and nothing re-sorts it here. A second ordering source
    // is exactly what #521 removed.
    const newestId = useMemo(
        () => (messages.length ? idOf(messages[messages.length - 1]) : null),
        [messages]
    );
    // #715 — the message the bell opened this thread at starts expanded too,
    // or "opens that message" would land on a one-line collapsed card.
    const { activeFocus: focusCommId } = useActiveBellFocus();
    const [expandOverrides, setExpandOverrides] = useState({});
    const isExpanded = useCallback(
        (id) => (
            Object.prototype.hasOwnProperty.call(expandOverrides, id)
                ? expandOverrides[id]
                : id === newestId || (focusCommId !== null && id === focusCommId)
        ),
        [expandOverrides, newestId, focusCommId]
    );
    const handleToggleCollapse = useCallback((entry, nextExpanded) => {
        const id = idOf(entry);
        if (!id) return;
        setExpandOverrides(prev => ({ ...prev, [id]: nextExpanded }));
    }, []);

    // Fetch-on-open when the stored row has no HTML body, whatever its source.
    const needsBody = Boolean(seed) && !seed.body_html;
    const externalId = seed?.external_id || seed?.email_id || '';
    const isGmailEmail =
        seed?._source === 'gmail' || externalId.startsWith('gmail_');
    const isOutlookEmail =
        seed?._source === 'outlook' || externalId.startsWith('outlook_');

    // #487 — the mailbox this thread arrived through, guessed from the seed
    // row's `_source` / external_id prefix. Since #509 (D1) this is a FALLBACK
    // only: the authoritative source is the row's mailbox_provider /
    // mailbox_address stamp (replyMailboxFor below), which every personal
    // writer sets. It still feeds usePersonalSenders' default selection for
    // the COMPOSE page and the thread-header pill.
    const threadProvider = isGmailEmail ? 'gmail' : isOutlookEmail ? 'outlook' : undefined;
    const {
        options, sendableOptions, selected, selectedAddress, sendFlags,
        statusUnknown,
        selectByOptionValue,
        isLoading: sendersLoading,
    } = usePersonalSenders(threadProvider);

    // #597 — called ONCE at the top level, for the same reason usePersonalSenders
    // is (see the note above): ReplyComposer renders inside the message map.
    const { data: templatesData } = useEmailTemplates();
    const { data: signaturesData } = useSignatures();

    // #509 (D1) — the mailbox that HOLDS the message being answered is the
    // only mailbox a reply may leave from. Authoritative when the row carries
    // the mailbox_provider/mailbox_address stamp; falls back to the #487
    // _source / external_id guess for pre-scope rows.
    const replyMailboxFor = useCallback((entry) => {
        const provider = entry?.mailbox_provider === 'google' ? 'gmail'
            : entry?.mailbox_provider === 'microsoft' ? 'outlook'
            : (entry?._source === 'gmail' || (entry?.external_id || entry?.email_id || '').startsWith('gmail_')) ? 'gmail'
            : (entry?._source === 'outlook' || (entry?.external_id || entry?.email_id || '').startsWith('outlook_')) ? 'outlook'
            : threadProvider;
        return { provider, address: (entry?.mailbox_address || '').toLowerCase() || null };
    }, [threadProvider]);

    // The locked reply target for the composer currently open: { commId,
    // provider, address }. Null until the user clicks Reply.
    const [replyTarget, setReplyTarget] = useState(null);

    // usePersonalSenders is still called (statuses, sendable flags) but its
    // SELECTION is not used for replies — the locked option matching the
    // target mailbox decides whether the reply can be sent.
    const lockedOption = useMemo(
        () => options.find(o => (
            o.provider === replyTarget?.provider
            && (!replyTarget?.address || o.address === replyTarget.address)
        )) || null,
        [options, replyTarget]
    );
    const lockedAddress = lockedOption?.address || replyTarget?.address || null;
    const lockedSendable = Boolean(lockedOption?.sendable);
    // D7: only an entirely undeterminable legacy row keeps the old sender
    // picker. A provider/source/prefix stamp always uses the locked option.
    const replyMailboxKnown = Boolean(replyTarget?.provider || replyTarget?.address);
    const replyOption = replyMailboxKnown ? lockedOption : selected;
    const replyAddress = replyOption?.address || lockedAddress || null;
    const replySendable = replyMailboxKnown
        ? lockedSendable
        : Boolean(replyOption?.sendable);

    // Thread-level mailbox (D8: every thread lives in one mailbox). Prefer the
    // backend's `mailbox` field on the thread response, else the seed row.
    const threadMailbox = useMemo(() => {
        const fromResponse = threadData?.mailbox
            || (threadData?.data ? threadData.data.mailbox : null);
        if (fromResponse?.provider) return fromResponse;
        if (seed) {
            const derived = replyMailboxFor(seed);
            if (derived.provider || derived.address) return derived;
        }
        return null;
    }, [replyMailboxFor, threadData, seed]);

    // Communications-sourced rows have `external_id` (e.g. "outlook_<graph_id>")
    // but no `id` or `email_id`. The detail hooks need the bare provider
    // message id, so strip the prefix; fall back to legacy fields for rows
    // that still carry them.
    const gmailId = isGmailEmail
        ? (seed?.external_id?.replace(/^gmail_/, '')
           || seed?.gmail_message_id
           || seed?.id
           || seed?.email_id?.replace(/^gmail_/, ''))
        : null;
    const outlookId = isOutlookEmail
        ? (seed?.external_id?.replace(/^outlook_/, '')
           || seed?.id
           || seed?.email_id?.replace(/^outlook_/, ''))
        : null;

    // A COLLAPSED message does not fetch its body — the request is deferred
    // until the card is expanded. On a long thread that is the difference
    // between one provider round-trip and one per message, and the body of a
    // card nobody has opened is never displayed anyway.
    const seedExpanded = isExpanded(idOf(seed));
    const { data: gmailDetail } = useGmailEmailDetail(
        gmailId, isGmailEmail && needsBody && seedExpanded
    );
    const { data: outlookDetail } = useOutlookEmailDetail(
        outlookId, isOutlookEmail && needsBody && seedExpanded
    );

    // The fetched body, normalised to { body_html, body } regardless of provider.
    //
    // Both detail endpoints now report `body_type` and return `body_html`
    // explicitly, so neither branch has to guess whether it received markup.
    // `body_html` is still read defensively because a cached response from
    // before that change carries only `body`.
    const fetchedBody = useMemo(() => {
        if (!needsBody) return null;
        const detail = isGmailEmail ? gmailDetail : isOutlookEmail ? outlookDetail : null;
        if (!detail?.body) return null;
        const isHtml =
            String(detail.body_type || '').toLowerCase() === 'html' ||
            Boolean(detail.body_html);
        return {
            body_html: detail.body_html || (isHtml ? detail.body : ''),
            body: detail.body_text || detail.body,
        };
    }, [needsBody, isGmailEmail, isOutlookEmail, gmailDetail, outlookDetail]);

    // Merge the fetched body into the message it belongs to, leaving the rest
    // of the thread untouched.
    const messagesWithBodies = useMemo(() => {
        if (!fetchedBody || !seed) return messages;
        return messages.map((m) =>
            m.comm_id === seed.comm_id || m === seed ? { ...m, ...fetchedBody } : m
        );
    }, [messages, fetchedBody, seed]);

    // Inline reply state (mirrors EmailDetailPage pattern)
    const [openComposerFor, setOpenComposerFor] = useState(null);
    const [replyMode, setReplyMode] = useState('reply');
    // #659 — who the anchored reply's header names, from resolveReplyTarget.
    const [replyName, setReplyName] = useState(null);
    const [replyBody, setReplyBody] = useState('');
    // #597 — { id, html } of the signature currently in the body. Owned here,
    // not in TemplateSignatureSelectors: the seed below and the send guard both
    // need the exact inserted HTML (validation finding V1).
    const [replySignature, setReplySignature] = useState({ id: '', html: '' });
    // #623 — attachments; owned here because the send path is here.
    const attachmentState = useAttachmentUpload();
    const [replyTo, setReplyTo] = useState([]);
    const [replyCc, setReplyCc] = useState([]);
    const [replyBcc, setReplyBcc] = useState([]);
    const [showCcBcc, setShowCcBcc] = useState(false);
    const replyEditorRef = useRef(null);

    // #521 — open on the newest message, the way every mail client does.
    //
    // The thread renders oldest-first and every card mounts expanded, so on a
    // long conversation the newest message starts below the fold.
    //
    // This used to set `scrollTop = scrollHeight` — jump the whole page to the
    // bottom — which on ANY thread, including a single-message one, scrolled
    // straight past the reader's own toolbar and subject. With the toolbar not
    // yet sticky, Back and every mail-state action went with them: the thread
    // opened with no visible way out of it.
    //
    // `scrollIntoView({ block: 'nearest' })` moves the minimum needed and does
    // nothing at all when the message is already on screen, so a short thread
    // opens at its top, where the subject is.
    //
    // `newestMessageRef` lands on the newest card's wrapper in the render
    // below. It was once declared and never attached, which made this whole
    // effect a silent no-op.
    const threadScrollRef = useRef(null);
    const newestMessageRef = useRef(null);
    //
    // #715 — when the bell opened this thread at a particular message,
    // `useBellFocus` scrolls to it once and otherwise falls back to this
    // newest-message scroll (see the hook for the once-only rules).
    const scrollToNewest = useCallback(() => {
        newestMessageRef.current?.scrollIntoView({ block: 'nearest' });
    }, []);
    useBellFocus({
        scrollRef: threadScrollRef,
        threadKey: commId,
        ready: !threadLoading,
        count: messagesWithBodies.length,
        onNoFocus: scrollToNewest,
    });
    // #509 — replies go through the dedicated personal reply endpoint; the
    // server picks the sending mailbox from the message row (D1).
    const replyMutation = useReplyToPersonalMessage();
    // #659 — reply-all starts a NEW thread (the prototype's "New thread"), so it
    // goes out as a new message through the personal send route, never the
    // reply endpoint, which threads it with In-Reply-To/References.
    const sendEmailMutation = useSendEmail();

    // #559 — opening the thread clears its unread state. Every message the
    // reader is now looking at counts as read, so collect the ones the server
    // still marks unread (tri-state: absent is_read means read) and mark them
    // in a single POST. The ref guard keys on the unread ids themselves, so
    // re-renders and refetches of the same thread don't re-POST; a remount
    // starts a fresh ref, but the server-side mark is idempotent.
    const markReadMutation = useMarkPersonalInboxRead();
    const markedUnreadKeyRef = useRef(null);
    useEffect(() => {
        if (threadLoading || !messagesWithBodies.length) return;
        const unreadIds = messagesWithBodies
            .filter((m) => m.is_read === false)
            .map((m) => m.comm_id || m.email_id || m.tracking_id)
            .filter(Boolean);
        if (!unreadIds.length) return;
        const key = unreadIds.join(',');
        if (markedUnreadKeyRef.current === key) return;
        markedUnreadKeyRef.current = key;
        // #715 — the server's row rule clears the bell entries for these;
        // tell TopBar so the badge drops without waiting for its poll.
        markReadMutation.mutate(unreadIds, { onSuccess: notifyBellChanged });
    }, [threadLoading, messagesWithBodies, markReadMutation]);

    // #715 D1 opener rule — also clear the bell for messages whose MAIL is
    // already read (so the effect above skips them) but whose entry predates
    // the row rule or the already-read skip. The UNREAD ones are left out:
    // the mark-read above clears their entries server-side (row rule), so
    // posting them here too would be a second request for the same rows
    // (audit F11). Each id is posted at most once per mount.
    const bellPostedIdsRef = useRef(new Set());
    useEffect(() => {
        if (threadLoading || !messagesWithBodies.length) return;
        const fresh = messagesWithBodies
            .filter((m) => m.comm_id && m.is_read !== false)
            .map((m) => m.comm_id)
            .filter((id) => !bellPostedIdsRef.current.has(id));
        if (!fresh.length) return;
        fresh.forEach((id) => bellPostedIdsRef.current.add(id));
        markMailNotificationsRead(fresh, getAuthHeader);
    }, [threadLoading, messagesWithBodies, getAuthHeader]);

    // The one close-and-clear, for ✕ / Discard, a re-press of the same
    // control, and a successful send. A stale draft, signature or file left
    // behind would surface in the next composer.
    const closeComposer = () => {
        setOpenComposerFor(null);
        setReplyBody(''); setReplySignature({ id: '', html: '' }); attachmentState.reset();
        setReplyTo([]); setReplyCc([]); setReplyBcc([]); setShowCcBcc(false);
    };

    const handleReply = (entry, mode = 'reply') => {
        // The same control again CLOSES the composer, as on the unified lane.
        // Re-running the open instead cleared the body without re-seeding the
        // signature (the seed key had not changed), wiping the draft and
        // leaving the tracked signature pointing at HTML no longer there.
        const composerKey = entry.comm_id || entry.email_id || entry.tracking_id || 'default';
        if (openComposerFor === composerKey && replyMode === mode) {
            closeComposer();
            return;
        }
        const replyEntry = {
            // from_address FIRST — the bare SMTP form. The old code seeded
            // from from_email, which the personal Outlook sync stores as a
            // display string ("Name <a@b>"); Graph accepts that on draft
            // create but rejects it at /send with a 400.
            from_address: entry.from_address || entry.from_email || entry.contact_email || entry.from,
            to_emails: entry.to_emails || [entry.to || entry.contact_email],
            cc_emails: entry.cc_emails || [],
            bcc_emails: entry.bcc_emails || [],
            // #659 — a reply to our OWN message goes to its recipients.
            direction: entry.direction,
        };
        // Both identities, de-duplicated: mail addressed to the receiving
        // mailbox AND to the CRM login alias is self.
        const ownAddresses = [...new Set(
            [replyMailboxFor(entry).address, selectedAddress, user?.email].filter(Boolean)
        )];
        // #659 — a plain reply has NO To row, so its recipient is resolved
        // before anything opens. With nobody to reply to, refuse rather than
        // open a composer that cannot send.
        const target = mode === 'reply'
            ? resolveReplyTarget(replyEntry, { ownAddresses, displayName: () => rowSenderName(entry) })
            : null;
        if (target?.reason) {
            toast.error(target.reason);
            return;
        }
        // D1: the reply is locked to the mailbox that received this message.
        setReplyTarget({ commId: entry.comm_id || entry.email_id || null, ...replyMailboxFor(entry) });
        const recipients = computeReplyRecipients(replyEntry, { mode, ownAddresses });
        setReplyTo(recipients.to.map(addr => ({ email: addr })));
        setReplyCc(recipients.cc.map(addr => ({ email: addr })));
        setReplyBcc(recipients.bcc.map(addr => ({ email: addr })));
        setShowCcBcc(recipients.cc.length > 0 || recipients.bcc.length > 0);
        setReplyBody('');
        // A different composer opening must not inherit the last one's files.
        attachmentState.reset();
        setReplyMode(mode);
        setReplyName(target?.name || null);
        setOpenComposerFor(composerKey);
    };

    /**
     * #597 — seed the signature when a composer opens (and when the open
     * composer switches between Reply and Reply all, because the key below
     * includes the mode).
     *
     * D10: this MUST wait for `sendersLoading`. `replyAddress` is
     * `replyOption?.address || lockedAddress || null` and is null until
     * usePersonalSenders settles, so seeding earlier would resolve
     * {{SenderEmail}} to an empty string in a message that really goes out.
     * `ContactEmailComposer` carries the same gate for the same reason.
     *
     * An effect rather than a line in handleReply, unlike the unified lane:
     * there the sending address is computed synchronously in the handler, here
     * it arrives asynchronously. The effect runs AFTER handleReply's
     * `setReplyBody('')`, so it cannot race that clear — and the functional
     * updater below never overwrites a body that already has content.
     */
    const seededForComposer = useRef(null);
    useEffect(() => {
        if (!openComposerFor) { seededForComposer.current = null; return; }
        if (sendersLoading) return;
        const key = `${openComposerFor}:${replyMode}`;
        if (seededForComposer.current === key) return;
        seededForComposer.current = key;

        const seed = pickSeedSignature({
            activeOverrideId: signaturesData?.activeOverrideId,
            signatures: signaturesData?.signatures,
            adminSignatures: signaturesData?.adminSignatures,
            defaultSignature: signaturesData?.defaultSignature,
        });
        // buildSignatureHtml is the SAME recipe TemplateSignatureSelectors uses,
        // so the component's strip matches this seed exactly and the first
        // switch replaces instead of stacking.
        const html = seed ? buildSignatureHtml(seed, user, replyAddress || '') : '';
        setReplySignature({ id: seed && html ? seed.signature_id : '', html });
        setReplyBody(prev => (prev ? prev : (html ? `<p></p><p></p>${html}` : '')));
    }, [openComposerFor, replyMode, sendersLoading, replyAddress, signaturesData, user]);

    const handleSubmitReply = async () => {
        const contract = modeContract(replyMode);
        const newThread = contract.threadBehaviour === 'new-thread';
        // Only the reply endpoint needs the row's commId; a new-thread send does not.
        if (!newThread && !replyTarget?.commId) { toast.error('Open a message to reply to'); return; }
        if (statusUnknown) {
            // #487: a failed or still-loading status query is NOT "no mailbox".
            // Falling through would log the reply to the CRM and report it as
            // sent, so the user believes a delivered reply exists.
            toast.error("We couldn't check your mailbox connection. Please retry in a moment.");
            return;
        }
        // D1/D2: the reply leaves from the mailbox that received the message.
        // If that mailbox cannot send, the user must reconnect it — never a
        // silent fallback to the other mailbox.
        if (!replySendable) {
            toast.error(`Reconnect ${replyAddress || 'your mailbox'} to reply from it.`);
            return;
        }
        // #597 — the body now opens PRE-SEEDED with a signature, so a bare
        // `!replyBody.trim()` check can never fail and a signature-only reply
        // would go out on a stray click. Anything the user typed still counts.
        if (!hasAuthoredText(replyBody)) { toast.error('Write a reply first'); return; }
        // #623 — never send over an unfinished or failed upload.
        if (attachmentState.pendingCount) { toast.error('Wait for attachments to finish uploading'); return; }
        if (attachmentState.failedCount) { toast.error('Remove or retry the attachments that failed to upload'); return; }
        if (!replyTo.length) { toast.error('Please add at least one recipient'); return; }
        // Derive contact_id / account_id from the thread's seed message so
        // replies are linked to the right CRM contact and account.
        //
        // Read from the `seed` memo above, not from `messages[0]`. A local
        // `const seed = messages[0]` shadowed it here and made the CRM linkage
        // depend on ARRAY POSITION — so when #521 flipped the thread to
        // oldest-first, the same reply would silently have been filed against a
        // different contact and account than the day before. The memo resolves
        // the message this view was opened on, which is a property of the
        // thread rather than of how it happens to be sorted.
        const contactId = seed?.contact_id || seed?.contactId || null;
        const accountId = seed?.account_id || seed?.accountId || null;

        // #659 — a new-thread mode (reply all) is a NEW message from the SAME
        // locked mailbox (#509 D1; D7's pick for an undeterminable legacy
        // row), under the read-only subject the card showed.
        if (newThread) {
            try {
                await sendEmailMutation.mutateAsync({
                    to: replyTo.map(c => c.email),
                    cc: replyCc.map(c => c.email),
                    bcc: replyBcc.map(c => c.email),
                    subject: threadSubject(contract, messagesWithBodies[0]?.subject),
                    body: replyBody,
                    provider: replyMailboxKnown ? replyTarget?.provider : replyOption?.provider,
                    ...sendFlags,
                    ...(contactId && { contact_id: contactId }),
                    ...(accountId && { account_id: accountId }),
                    ...(attachmentState.payload.length && { attachments: attachmentState.payload }),
                    // Never fall back to a CRM-only log row: this is a send
                    // the user expects delivered. With no route it throws
                    // (describeSendError names it) and the draft stays open.
                    allowLogFallback: false,
                });
                toast.success(`Sent to everyone as a new thread, from ${replyAddress}`);
                closeComposer();
            } catch (err) {
                toast.error(describeSendError(err));
            }
            return;
        }

        try {
            // No `subject` — the server derives "Re: …" once from the original
            // (a second reply was becoming "Re: Re: …"). No `from`; a `provider`
            // is sent only for D7's fully undeterminable legacy-row fallback.
            const res = await replyMutation.mutateAsync({
                commId: replyTarget.commId,
                to: replyTo.map(c => c.email),
                body: replyBody,
                // Typeahead state stores recipient objects; the API contract
                // accepts bare strings. Convert all three recipient fields so
                // Reply All with CC/BCC does not fail Pydantic validation.
                cc: replyCc.map(c => c.email),
                bcc: replyBcc.map(c => c.email),
                body_type: 'HTML',
                ...(contactId && { contact_id: contactId }),
                ...(accountId && { account_id: accountId }),
                // D7 only: the server may use a provider hint when the legacy
                // row has no provider identity of its own. Known rows ignore it.
                ...(!replyMailboxKnown && replyOption?.provider && { provider: replyOption.provider }),
                // #623 — staged object keys, not bytes; the server fetches them.
                ...(attachmentState.payload.length && { attachments: attachmentState.payload }),
            });
            toast.success(`Reply sent from ${res?.sent_as || replyAddress}`);
            closeComposer();
        } catch (err) {
            toast.error(describeSendError(err));
        }
    };

    const handleForward = (entry) => {
        const senderName = entry.contact_name || entry.from || entry.from_email || 'Unknown';
        const forwardHeader = buildForwardHeader({
            fromAddress: entry.contact_email || entry.from || entry.from_email,
            fromName: senderName,
            date: entry.sent_at || entry.date || entry.scheduled_date || entry.comm_date,
            subject: entry.subject,
            bodyHtml: entry.body_html || entry.body,
            body: entry.snippet || entry.body,
            zone: resolveDisplayZone({ user }),
        });
        // Use router state (not URL params) to avoid URL length limits
        navigate('/inbox/compose', {
            state: {
                subject: `Fwd: ${entry.subject || ''}`,
                body: forwardHeader,
            },
        });
    };

    // ── Reader state (plan §12 / N1) ───────────────────────────────────────
    //
    // Every mutation is the shared `useMailState` family: one target shape, one
    // privilege boundary, resolved server-side from the same `inbox_id` field.
    // The personal lane omits `inbox_id` entirely — that absence IS the lane,
    // which is why nothing here branches on a surface kind.
    const starMail = useStarMail();
    const importantMail = useImportantMail();
    const archiveMail = useArchiveMail();
    const snoozeMail = useSnoozeMail();
    const moveMail = useMoveMail();
    const readMail = useReadMail();
    // The rail's model, for the Move menu. Same query key the rail uses, so
    // this shares its cache rather than issuing a second request.
    const { data: folderModel } = useMailFolders(null);

    // The message the reader is anchored on: the row the URL names, falling
    // back to the newest. Chips, the star's scope hint and the open-in-provider
    // control all read ITS origin — per message, never per surface, because in
    // a pooled thread adjacent messages legitimately open in different
    // providers (N3).
    const anchor = seed || messagesWithBodies[messagesWithBodies.length - 1] || null;
    const anchorOrigin = anchor?.origin || null;
    const threadIds = messagesWithBodies.map(idOf).filter(Boolean);
    // #721 — which way the Archive control writes. Opened from Archived it is
    // Move to Inbox; a static Archive there was a no-op that toasted success.
    const archived = useMemo(
        () => conversationArchived(messagesWithBodies),
        [messagesWithBodies]
    );
    // Whether Move to → Inbox is a restore (a bin) or a no-op (archived, not in a
    // bin): the toolbar withholds it in the second case.
    const binned = useMemo(
        () => conversationBinned(messagesWithBodies),
        [messagesWithBodies]
    );

    // #646 — tags. The toolbar tags the CONVERSATION (every message, expanded
    // server-side); a message's ⋯ menu tags that message alone.
    const tagMail = useTagMail();
    const conversationTags = useMemo(
        () => conversationTagsOf(messagesWithBodies),
        [messagesWithBodies]
    );
    const changeTags = useCallback(
        async (ids, { add = [], remove = [] }, expand) => {
            if (!ids.length || (!add.length && !remove.length)) return;
            try {
                const res = await tagMail.mutateAsync({
                    target: { ids: ids.slice(0, 200) },
                    add,
                    remove,
                    expand_conversations: expand,
                });
                reportTagResult(res?.data, { verb: 'Tags updated', noun: 'message' });
            } catch (err) {
                toast.error(tagErrorMessage(err));
            }
        },
        [tagMail]
    );

    // The address this thread was RECEIVED on. The origin's stamp first (it is
    // the row's own mailbox), then the thread-level #509 mailbox.
    const receivingAddress = anchorOrigin?.mailbox_address
        || anchor?.mailbox_address
        || threadMailbox?.address
        || null;

    // "External" = the counterparty is outside the receiving mailbox's domain.
    //
    // Derived here on purpose, and only here: there is no external/internal
    // flag anywhere in `communications`, and this is a display hint with no
    // action attached. It is a domain comparison over two addresses this view
    // already has — NOT provider identity, which is never derived client-side.
    // Either address missing ⇒ no chip, rather than a guess.
    const inboundAnchor = anchor && anchor.direction !== 'outbound';
    const senderDomain = domainOf(anchor?.from_address || anchor?.from_email);
    const mailboxDomain = domainOf(receivingAddress);
    const isExternalThread = Boolean(
        inboundAnchor && senderDomain && mailboxDomain && senderDomain !== mailboxDomain
    );

    // Optional list context, handed over by the row that opened this thread:
    // `{ position, total, prevCommId, nextCommId }`. Absent on a deep link, a
    // refresh or the cache-fallback path — and then the counter does NOT render
    // and prev/next disable, rather than a position guessed from whichever page
    // of rows happens to be cached.
    const threadNav = location.state?.threadNav || null;

    const goBack = useCallback(() => {
        if (window.history.state && window.history.state.idx > 0) navigate(-1);
        else navigate('/inbox');
    }, [navigate]);

    // Starring a CONVERSATION stars its messages, so the list row — which is
    // built from the thread's ROOT — flips too. Reading it back as "any message
    // starred" is the same rule in reverse; the two halves have to agree or the
    // star appears to undo itself on the next refetch.
    const [starOverride, setStarOverride] = useState(null);
    const threadStarred = starOverride ?? messagesWithBodies.some(m => m.is_starred === true);
    // The Important marker, mirroring the star exactly: an optimistic
    // override so the icon answers the click, reconciled from the thread on
    // the next fetch. "Any message important" is the same rule the list row
    // reads, so the two cannot disagree about a thread's state.
    const [importantOverride, setImportantOverride] = useState(null);
    const threadImportant =
        importantOverride ?? messagesWithBodies.some(m => m.is_important === true);
    const handleImportant = () => {
        if (!threadIds.length) return;
        const next = !threadImportant;
        setImportantOverride(next);
        importantMail.mutate(
            { target: { ids: threadIds }, important: next },
            {
                onError: () => {
                    setImportantOverride(!next);
                    toast.error("Couldn't update Important");
                },
            }
        );
    };

    const handleStar = () => {
        if (!threadIds.length) return;
        const next = !threadStarred;
        setStarOverride(next);
        starMail.mutate(
            { target: { ids: threadIds }, starred: next },
            {
                onError: () => {
                    setStarOverride(!next);
                    toast.error("Couldn't update the star");
                },
            }
        );
    };

    const handleMarkUnread = () => {
        if (!threadIds.length) return;
        readMail.mutate(
            { target: { ids: threadIds }, read: false },
            {
                onSuccess: () => { toast.success('Marked as unread'); goBack(); },
                onError: () => toast.error("Couldn't mark this conversation unread"),
            }
        );
    };

    const handleArchive = () => {
        if (!threadIds.length) return;
        archiveMail.mutate(
            { target: { ids: threadIds }, archived: !archived },
            {
                onSuccess: (res) => {
                    // `onSuccess` fires on any 2xx, so a write that changed
                    // nothing must not report success — the same rule the row
                    // strip and the bulk bar follow (#710).
                    if (res?.data?.modified === 0) {
                        toast.warning('Nothing changed — the message may have moved already');
                        return;
                    }
                    toast.success(archived ? 'Moved to Inbox' : 'Conversation archived');
                    goBack();
                },
                onError: () => toast.error(archived
                    ? "Couldn't move this conversation to Inbox"
                    : "Couldn't archive this conversation"),
            }
        );
    };

    // Delete is a MOVE to trash, not a destructive write — the same thing the
    // rail counts under Trash. There is no hard-delete endpoint in this family
    // and this control must not invent one.
    const handleDelete = () => {
        if (!threadIds.length) return;
        moveMail.mutate(
            { target: { ids: threadIds }, folder: 'trash' },
            {
                onSuccess: () => { toast.success('Conversation moved to Trash'); goBack(); },
                onError: () => toast.error("Couldn't delete this conversation"),
            }
        );
    };

    // The newest message — the one the thread-level Reply all answers, and the
    // one the view scrolls to on open. Last, because the thread is oldest-first
    // and that order is the server's (see the collapse note above).
    const newestMessage = messagesWithBodies.length
        ? messagesWithBodies[messagesWithBodies.length - 1]
        : null;

    // The `⋯` Delete on ONE card. Same move-to-trash as the toolbar's Delete,
    // scoped to a single row; only leaves the reader when nothing is left.
    const handleDeleteMessage = (entry) => {
        const id = idOf(entry);
        if (!id) return;
        const wasLast = threadIds.length <= 1;
        moveMail.mutate(
            { target: { ids: [id] }, folder: 'trash' },
            {
                onSuccess: () => {
                    toast.success('Message moved to Trash');
                    if (wasLast) goBack();
                },
                onError: () => toast.error("Couldn't delete this message"),
            }
        );
    };

    const handleMove = (folder) => {
        if (!threadIds.length) return;
        moveMail.mutate(
            { target: { ids: threadIds }, folder },
            {
                onSuccess: (res) => {
                    // `onSuccess` fires on any 2xx; a move that changed nothing
                    // must not report success (the same rule as Archive above).
                    if (res?.data?.modified === 0) {
                        toast.warning('Nothing changed — the message may have moved already');
                        return;
                    }
                    toast.success(folder ? `Moved to ${folder}` : 'Moved to Inbox');
                    goBack();
                },
                onError: () => toast.error("Couldn't move this conversation"),
            }
        );
    };

    const handleSnooze = (presetKey) => {
        // The preset is resolved HERE, in the viewer's clock, and sent as an
        // absolute instant. The API accepts no symbolic preset by design.
        // The viewer's DISPLAY zone, not the browser's: "tomorrow 09:00"
        // must mean 09:00 in the zone they are reading the mailbox in.
        const until = resolveSnoozePreset(presetKey, new Date(), resolveDisplayZone({ user }));
        if (!until || !threadIds.length) return;
        snoozeMail.mutate(
            { target: { ids: threadIds }, until: until.toISOString() },
            {
                onSuccess: () => { toast.success('Snoozed'); goBack(); },
                onError: () => toast.error("Couldn't snooze this conversation"),
            }
        );
    };

    // "Forward all — whole thread" forwards the WHOLE thread. Forwarding only
    // the newest message under that label is the same class of lie as #596's
    // dead link: the control says what it does, so it has to do it.
    const handleForwardAll = () => {
        if (!messagesWithBodies.length) return;
        const zone = resolveDisplayZone({ user });
        const body = messagesWithBodies
            .map((entry) => buildForwardHeader({
                fromAddress: entry.contact_email || entry.from_address || entry.from_email,
                fromName: entry.contact_name || entry.from_name || entry.from_email || 'Unknown',
                date: entry.sent_at || entry.date || entry.scheduled_date || entry.comm_date,
                subject: entry.subject,
                bodyHtml: entry.body_html || entry.body,
                body: entry.snippet || entry.body,
                zone,
            }))
            .join('<hr />');
        navigate('/inbox/compose', {
            state: { subject: `Fwd: ${anchor?.subject || ''}`, body },
        });
    };

    // Loading state
    if (threadLoading && !messagesWithBodies.length) {
        return (
            <div className="flex flex-col items-center justify-center h-full py-20">
                <InboxSpinner label="Loading thread…" />
            </div>
        );
    }

    // #803 — deleted: no Retry, a retry can only 404 again.
    if (threadGone && !messagesWithBodies.length) {
        return (
            <div className="flex flex-col items-center justify-center h-full py-20" data-testid="thread-deleted">
                <InboxError
                    message="This email was deleted."
                    detail="It's no longer in your inbox or Trash."
                />
                <Button variant="outline" size="sm" onClick={() => navigate('/inbox')} className="mt-2">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to inbox
                </Button>
            </div>
        );
    }

    // Error state — but we have a cache fallback
    if (threadError && !messagesWithBodies.length) {
        return (
            <div className="flex flex-col items-center justify-center h-full py-20">
                <InboxError message="Couldn't load this thread." onRetry={() => refetch()} />
                <Button variant="outline" size="sm" onClick={() => navigate('/inbox')} className="mt-2">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to inbox
                </Button>
            </div>
        );
    }

    if (!messagesWithBodies.length) {
        return (
            <div className="flex flex-col items-center justify-center h-full py-20">
                <InboxError message="Email not found. This email may have been moved or deleted." />
                <Button variant="outline" size="sm" onClick={() => navigate('/inbox')} className="mt-2">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to inbox
                </Button>
            </div>
        );
    }

    const messageCount = messagesWithBodies.length;

    // ── The one open composer (#659) ───────────────────────────────────────
    //
    // Where it renders is the mode contract's call: `reply` is anchored in
    // the replied-to message's slot, `reply_all` is a thread-level card after
    // the last message. One `openComposerFor`, so the two are exclusive.
    const composerAnchored = modeContract(replyMode).anchored;
    const composerEntry = openComposerFor
        ? messagesWithBodies.find(entry => idOf(entry) === openComposerFor) || null
        : null;
    // #659 D3 — identity is the footer's. A From CHOICE exists only for the
    // D7 undeterminable legacy row, and only while its composer is open;
    // every other reply is locked to the receiving mailbox (#509 D1).
    const composerOpen = Boolean(openComposerFor);
    const footerHasChoice = composerOpen && !replyMailboxKnown && options.length > 1;
    const footerProvider = composerOpen ? replyTarget?.provider : threadMailbox?.provider;
    // With a composer open the footer names the address it will ACTUALLY
    // send from (a D7 pick included); otherwise, the thread's mailbox.
    const footerReplyingAs = (composerOpen ? replyAddress : threadMailbox?.address) || null;
    const footerIdentityState = (sendersLoading || statusUnknown)
        ? 'checking'
        : (composerOpen && !replyAddress ? 'none' : null);

    // One function, two render sites (the message slot and the thread-level
    // slot), so they cannot drift. A plain function returning JSX — not a
    // component and not a hook — so the rules of hooks are untouched.
    const renderComposer = (entry) => {
        const entryId = idOf(entry);
        return (
            <ReplyComposer
                key={`${entryId}:${replyMode}`}
                composerId={entryId}
                /* Fills `{name}` / `{subject}` / `{n}` in the mode header.
                   `name` is resolveReplyTarget's, so a reply to our own
                   message names its recipient, never us. `{n}` counts the
                   whole thread. */
                modeVars={{
                    name: replyName || rowSenderName(entry),
                    subject: messagesWithBodies[0]?.subject || '',
                    n: messagesWithBodies.length,
                }}
                mode={replyMode}
                to={replyTo}
                setTo={setReplyTo}
                cc={replyCc}
                setCc={setReplyCc}
                bcc={replyBcc}
                setBcc={setReplyBcc}
                showCcBcc={showCcBcc}
                setShowCcBcc={setShowCcBcc}
                body={replyBody}
                setBody={setReplyBody}
                reconnectRequired={replyMailboxKnown && Boolean(replyTarget) && !replySendable && !sendersLoading}
                reconnectHref="/settings/integrations"
                // The mutation that is actually in flight for this mode.
                reply={composerAnchored ? replyMutation : sendEmailMutation}
                subject={threadSubject(modeContract(replyMode), messagesWithBodies[0]?.subject)}
                onSubmit={handleSubmitReply}
                onClose={closeComposer}
                editorRef={replyEditorRef}
                sendDisabled={sendersLoading || !replySendable}
                // #597 — fetched once at this view's top level and passed down.
                templates={templatesData?.templates}
                adminTemplates={templatesData?.adminTemplates}
                signatures={signaturesData?.signatures}
                adminSignatures={signaturesData?.adminSignatures}
                activeOverrideId={signaturesData?.activeOverrideId}
                user={user}
                // replyAddress — the mailbox that RECEIVED this message and
                // therefore sends the reply (#509 D1). NOT the footer's option
                // value, which on this lane is a provider id.
                senderEmail={replyAddress || ''}
                signature={replySignature}
                onSignatureChange={setReplySignature}
                attachments={attachmentState.attachments}
                onAddAttachments={attachmentState.addAttachments}
                onRemoveAttachment={attachmentState.remove}
                selectorsDisabled={sendersLoading}
            />
        );
    };

    return (
        <ThreadReaderSurface
            testId="personal-thread-view"
            scrollRef={threadScrollRef}
            subject={messagesWithBodies[0]?.subject || '(No subject)'}
            starred={threadStarred}
            onStar={handleStar}
            important={threadImportant}
            onImportant={handleImportant}
            starTitle={mailStateScopeHint(anchorOrigin, threadStarred ? 'Unstar' : 'Star') || undefined}
            threadCount={`${messageCount} message${messageCount !== 1 ? 's' : ''} in this thread`}
            toolbar={
                /* `POST /api/mail/read` with `read: false` is the unread half —
                   it did not exist when this toolbar was written, which is why
                   the control used to render disabled with a tooltip denying the
                   capability. It exists now, so it is wired. */
                <ThreadReaderToolbar
                    surface={SURFACES.personal}
                    origin={anchorOrigin}
                    onBack={goBack}
                    onArchive={handleArchive}
                    archived={archived}
                    binned={binned}
                    onMarkUnread={handleMarkUnread}
                    onDelete={handleDelete}
                    onSnooze={handleSnooze}
                    onMove={handleMove}
                    folderModel={folderModel}
                    position={threadNav?.position}
                    total={threadNav?.total}
                    onPrev={threadNav?.prevId
                        ? () => navigate(`/inbox/email/${threadNav.prevId}`)
                        : undefined}
                    onNext={threadNav?.nextId
                        ? () => navigate(`/inbox/email/${threadNav.nextId}`)
                        : undefined}
                    onPrint={() => window.print()}
                    conversationTags={conversationTags}
                    onChangeConversationTags={(delta) => changeTags(threadIds, delta, true)}
                />
            }
            chips={
                /* The provider chip comes from the SERVER-resolved origin.
                   `threadMailbox`'s ProviderBadge survives only as the fallback
                   for rows that predate N3 or arrive through the React Query
                   cache without an `origin` — it is the #509 reply stamp this
                   view already depends on, not a second derivation of provider
                   identity. */
                <span className="flex items-center gap-[7px] min-w-0"
                      data-testid="thread-mailbox-pill">
                    {providerChip(anchorOrigin) ? (
                        <ReaderChip testId="thread-provider-chip">
                            {providerChip(anchorOrigin)}
                        </ReaderChip>
                    ) : (
                        threadMailbox && <ProviderBadge provider={threadMailbox.provider} />
                    )}
                    {isExternalThread && (
                        <ReaderChip
                            tone="warn"
                            testId="thread-external-chip"
                            title="The sender is outside this mailbox's domain"
                        >
                            External
                        </ReaderChip>
                    )}
                    {receivingAddress && (
                        <ReaderChip testId="thread-receiving-address">
                            {receivingAddress}
                        </ReaderChip>
                    )}
                </span>
            }
            footer={
                /* N5 — no plain Reply here: reply is per message, on the `↩` in a
                   message header, where the user can see WHICH message they are
                   answering. The thread-level actions answer/forward the whole
                   conversation, and the annotations inside the buttons say so. */
                <ThreadFooter
                    onReplyAll={newestMessage ? () => handleReply(newestMessage, 'reply_all') : undefined}
                    onForwardAll={messagesWithBodies.length ? handleForwardAll : undefined}
                    replyingAs={footerReplyingAs}
                    fromOptions={footerHasChoice ? options : undefined}
                    fromValue={replyOption?.value || replyOption?.address || undefined}
                    onFromChange={footerHasChoice ? selectByOptionValue : undefined}
                    renderBadge={(opt) => <ProviderBadge provider={opt.provider} />}
                    fromBadge={footerProvider ? <ProviderBadge provider={footerProvider} /> : null}
                    lockedHint={footerProvider
                        ? 'Replies go out from the mailbox that received this message.'
                        : undefined}
                    identityState={footerIdentityState}
                />
            }
        >
            {/* Thread messages */}
            <div data-testid="personal-thread-messages">
                {messagesWithBodies.map((entry) => {
                    const entryId = idOf(entry);
                    // #521 — every card gets the same wrapper and only the
                    // newest gets the ref. Wrapping the newest alone would
                    // change that slot's element type whenever a reply lands,
                    // remounting the card and dropping its open menu.
                    return (
                        <div
                            key={entryId}
                            ref={entryId === newestId ? newestMessageRef : undefined}
                        >
                            <ThreadMessage
                                entry={entry}
                                inbox={null}
                                canReplyAllowed={true}
                                onReply={(e) => handleReply(e, 'reply')}
                                onReplyAll={(e) => handleReply(e, 'reply_all')}
                                onForward={handleForward}
                                // Controlled: this view has to know what is open,
                                // because a collapsed message must not fetch.
                                collapsed={!isExpanded(entryId)}
                                onToggleCollapse={handleToggleCollapse}
                                onDelete={handleDeleteMessage}
                                onChangeTags={(msg, delta) => changeTags([idOf(msg)].filter(Boolean), delta, false)}
                                getAuthHeader={getAuthHeader}
                                onDownloadAttachment={downloadAttachment}
                                // No `onMarkUnread` — see the toolbar note above.
                                // The `⋯` item disables itself.
                                composer={openComposerFor === entryId && composerAnchored
                                    ? renderComposer(entry)
                                    : null}
                            />
                        </div>
                    );
                })}
                {/* #659 D2 — Reply all is a THREAD-level composer, after the
                    last message, never inside one. */}
                {composerEntry && !composerAnchored && renderComposer(composerEntry)}
            </div>

        </ThreadReaderSurface>
    );
}
